import { PlusIcon, TrashIcon } from '@heroicons/react/24/outline'
import { useEffect, useState } from 'react'
import { toast } from 'react-toastify'
import { createLoanCollateral, deleteLoanCollateral, getLoanCollateral, updateLoanCollateral } from 'services'
import { Button } from 'stories/components'
import { confirm } from 'utils'

/** One parcel as the register returns it. */
export interface CollateralRow {
  id: number
  sortOrder: number
  address: string
  description: string
  propertyType: string
  value: number
  allocatedAmount: number
  minimumReleasePrice: number
  /** What it costs to release, resolved by the backend so the screen cannot disagree with it. */
  releasePrice: number
  releasedDate: string
  releaseAmount: number
  releasedBy: string
  paymentId: number | null
}

export interface CollateralSummary {
  total: number
  released: number
  remaining: number
  allocatedTotal: number
  allocatedRemaining: number
  allocatedReleased: number
  valueRemaining: number
}

const money = (n: any) =>
  `$${(Number(n) || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`

const toNumber = (value: any) => {
  const n = typeof value === 'string' ? parseFloat(value.replace(/[^0-9.-]/g, '')) : Number(value)
  return isNaN(n) ? 0 : n
}

const isReleased = (row: CollateralRow) => !!`${row.releasedDate || ''}`.trim()

/**
 * The loan's collateral register, on the screen that uses it.
 *
 * A blanket loan's parcels and their release prices are only ever consulted when one of them is
 * paying off, so the register lives beside the partial payoff rather than behind another menu
 * entry. It is seeded from the loan file on first read, so a loan originated here already lists
 * its properties and only the allocated amounts need filling in.
 */
export default function CollateralPanel({
  canEdit,
  onLoaded,
  reloadKey,
}: {
  canEdit: boolean
  /** Lets the Payoff screen offer the same rows in its collateral picker. */
  onLoaded?: (rows: CollateralRow[]) => void
  /** Bumped by the parent after a release is posted, to pull the released parcel through. */
  reloadKey?: number
}) {
  const [rows, setRows] = useState<CollateralRow[]>([])
  const [summary, setSummary] = useState<CollateralSummary | null>(null)
  const [open, setOpen] = useState(false)
  const [busy, setBusy] = useState(false)
  // Edits are held locally and written on blur, so typing an amount does not fire a save per
  // keystroke; `edits` holds only the fields actually touched.
  const [edits, setEdits] = useState<Record<string, string>>({})

  const load = async () => {
    try {
      const res = await getLoanCollateral()
      const next: CollateralRow[] = res.collateral || []
      setRows(next)
      setSummary(res.summary || null)
      onLoaded?.(next)
    } catch {
      // The register is reference data; failing to read it must not take the Payoff screen down.
    }
  }

  // Reloads when the parent bumps `reloadKey` after a release is posted. The react-hooks plugin is
  // not loaded in this config, so there is no exhaustive-deps directive to add here - and adding
  // one would be reported as an undefined rule and break the build.
  useEffect(() => {
    load()
  }, [reloadKey])

  const editKey = (id: number, field: string) => `${id}:${field}`

  const onFieldChange = (id: number, field: string, value: string) =>
    setEdits((prev) => ({ ...prev, [editKey(id, field)]: value }))

  const onFieldBlur = async (row: CollateralRow, field: 'address' | 'description' | 'allocatedAmount' | 'minimumReleasePrice') => {
    const key = editKey(row.id, field)
    if (!(key in edits)) return
    const raw = edits[key]
    const next = field === 'address' || field === 'description' ? raw : toNumber(raw)
    // Nothing to write when the value came back to where it started.
    if (`${next}` === `${(row as any)[field] ?? ''}`) {
      setEdits((prev) => {
        const copy = { ...prev }
        delete copy[key]
        return copy
      })
      return
    }
    setBusy(true)
    try {
      const res = await updateLoanCollateral(row.id, { [field]: next })
      if (!res?.success) toast(res?.message || 'Could not save the collateral.', { type: 'error' })
      setEdits((prev) => {
        const copy = { ...prev }
        delete copy[key]
        return copy
      })
      await load()
    } finally {
      setBusy(false)
    }
  }

  const onAdd = async () => {
    setBusy(true)
    try {
      const res = await createLoanCollateral({ address: '' })
      if (!res?.success) toast(res?.message || 'Could not add the collateral.', { type: 'error' })
      await load()
      setOpen(true)
    } finally {
      setBusy(false)
    }
  }

  const onRemove = async (row: CollateralRow) => {
    const confirmed = await confirm(
      `Remove ${row.address || 'this collateral'} from the register? This does not release any lien.`,
    )
    if (!confirmed) return
    setBusy(true)
    try {
      const res = await deleteLoanCollateral(row.id)
      if (!res?.success) toast(res?.message || 'Could not remove the collateral.', { type: 'error' })
      await load()
    } finally {
      setBusy(false)
    }
  }

  const fieldValue = (row: CollateralRow, field: string, fallback: any) => {
    const key = editKey(row.id, field)
    return key in edits ? edits[key] : `${fallback ?? ''}`
  }

  const headline = summary
    ? `${summary.remaining} securing this loan${summary.released ? `, ${summary.released} released` : ''}`
    : ''

  return (
    <div className="mb-4 border border-gray-200 rounded-lg bg-white">
      <div
        className="flex items-center justify-between gap-3 px-4 py-3 cursor-pointer"
        onClick={() => setOpen((prev) => !prev)}
      >
        <div>
          <h3 className="font-semibold text-gray-800">Collateral</h3>
          <p className="text-xs text-gray-600 mt-0.5">
            {headline || 'The properties securing this loan.'}
            {summary && summary.allocatedRemaining > 0
              ? ` · ${money(summary.allocatedRemaining)} of principal allocated to what remains`
              : ''}
          </p>
        </div>
        <span className="text-sm text-shade-blue">{open ? 'Hide' : 'Show'}</span>
      </div>

      {open && (
        <div className="px-4 pb-4">
          {rows.length === 0 ? (
            <div className="text-sm text-gray-500 italic py-2">
              No collateral is recorded on this loan yet. Add each property that secures it.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-xs text-gray-500 border-b border-gray-200">
                    <th className="text-left font-medium py-2 pr-3">Property</th>
                    <th className="text-right font-medium py-2 px-3 whitespace-nowrap">Allocated Principal</th>
                    <th className="text-right font-medium py-2 px-3 whitespace-nowrap">Release Price</th>
                    <th className="text-left font-medium py-2 px-3 whitespace-nowrap">Status</th>
                    <th className="w-6" />
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row) => {
                    const released = isReleased(row)
                    const editable = canEdit && !released
                    return (
                      <tr key={row.id} className={`border-b border-gray-100 ${released ? 'bg-gray-50' : ''}`}>
                        <td className="py-2 pr-3 align-top">
                          <input
                            className="w-full border border-gray-200 rounded px-2 py-1 text-sm disabled:bg-transparent disabled:border-transparent disabled:px-0"
                            placeholder="Property address"
                            disabled={!editable}
                            value={fieldValue(row, 'address', row.address)}
                            onChange={(e) => onFieldChange(row.id, 'address', e.target.value)}
                            onBlur={() => onFieldBlur(row, 'address')}
                          />
                          <input
                            className="w-full border border-gray-200 rounded px-2 py-1 text-xs text-gray-600 mt-1 disabled:bg-transparent disabled:border-transparent disabled:px-0"
                            placeholder="Legal / lot description (printed on the release demand)"
                            disabled={!editable}
                            value={fieldValue(row, 'description', row.description)}
                            onChange={(e) => onFieldChange(row.id, 'description', e.target.value)}
                            onBlur={() => onFieldBlur(row, 'description')}
                          />
                        </td>
                        <td className="py-2 px-3 align-top text-right">
                          <input
                            className="w-32 border border-gray-200 rounded px-2 py-1 text-sm text-right disabled:bg-transparent disabled:border-transparent disabled:px-0"
                            disabled={!editable}
                            value={fieldValue(row, 'allocatedAmount', row.allocatedAmount || '')}
                            onChange={(e) => onFieldChange(row.id, 'allocatedAmount', e.target.value)}
                            onBlur={() => onFieldBlur(row, 'allocatedAmount')}
                          />
                        </td>
                        <td className="py-2 px-3 align-top text-right">
                          <input
                            className="w-32 border border-gray-200 rounded px-2 py-1 text-sm text-right disabled:bg-transparent disabled:border-transparent disabled:px-0"
                            placeholder="= allocated"
                            disabled={!editable}
                            value={fieldValue(row, 'minimumReleasePrice', row.minimumReleasePrice || '')}
                            onChange={(e) => onFieldChange(row.id, 'minimumReleasePrice', e.target.value)}
                            onBlur={() => onFieldBlur(row, 'minimumReleasePrice')}
                          />
                          {!row.minimumReleasePrice && row.allocatedAmount ? (
                            <div className="text-[11px] text-gray-500 mt-0.5">{money(row.releasePrice)}</div>
                          ) : null}
                        </td>
                        <td className="py-2 px-3 align-top whitespace-nowrap">
                          {released ? (
                            <div className="text-xs text-gray-700">
                              <div className="font-medium">Released {row.releasedDate}</div>
                              <div>{money(row.releaseAmount)} to principal</div>
                              {row.releasedBy && <div className="text-gray-500">by {row.releasedBy}</div>}
                            </div>
                          ) : (
                            <span className="text-xs text-green-700">Securing the loan</span>
                          )}
                        </td>
                        <td className="py-2 align-top">
                          {editable && (
                            <span
                              className="flex justify-center text-red-500 hover:cursor-pointer"
                              onClick={() => onRemove(row)}
                            >
                              <TrashIcon className="w-4 h-4" />
                            </span>
                          )}
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          )}

          {canEdit && (
            <div className="mt-3 flex items-center gap-3">
              <Button link color="shade-blue" onClick={onAdd} disabled={busy}>
                <span className="flex items-center gap-1">
                  <PlusIcon className="w-4 h-4" /> Add collateral
                </span>
              </Button>
              <span className="text-xs text-gray-500">
                Release Price is what must be paid to principal to release that lien. Left blank, the allocated
                principal is the price.
              </span>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
